import React, { useEffect, useState } from 'react';
import { styled } from 'styled-components';
import CompassHeader, { PageType } from 'Compass/components/CompassHeader';
import {
  revereFeatureFlagCommand,
  revereGetFeatureFlagNames,
  revereGetFeatureFlagState,
  revereGetTimerTaskNames,
  revereGetTimerTaskState,
  revereTimerTaskCommand
} from 'Common/api/DazzleInternalService';
import {
  GetFeatureFlagStateResponse,
  GetTimerTaskStateResponse
} from '@dazzle/api-client';

type ControlType = 'feature-flag' | 'timer-task';
type FeatureFlagAction = 'ENABLE' | 'DISABLE' | 'ADD_TO_WHITELIST' | 'REMOVE_FROM_WHITELIST' | 'ADD_TO_BLACKLIST' | 'REMOVE_FROM_BLACKLIST';
type TimerTaskAction = 'ENABLE' | 'DISABLE' | 'SET_FREQUENCY';

const ReverePage = () => {
  const [controlType, setControlType] = useState<ControlType>('feature-flag');
  const [itemName, setItemName] = useState<string>('');
  const [featureFlags, setFeatureFlags] = useState<string[]>([]);
  const [timerTasks, setTimerTasks] = useState<string[]>([]);
  const [featureFlagAction, setFeatureFlagAction] = useState<FeatureFlagAction>('ENABLE');
  const [timerTaskAction, setTimerTaskAction] = useState<TimerTaskAction>('ENABLE');
  const [inputValue, setInputValue] = useState<string>('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentState, setCurrentState] = useState<GetFeatureFlagStateResponse | GetTimerTaskStateResponse | null>(null);

  useEffect(() => {
    const fetchNames = async () => {
      const flags = await revereGetFeatureFlagNames();
      const tasks = await revereGetTimerTaskNames();

      if (flags?.feature_flag_names) {
        setFeatureFlags(flags.feature_flag_names);
        if (flags.feature_flag_names.length > 0) {
          setItemName(flags.feature_flag_names[0]);
        }
      }

      if (tasks?.timer_task_names) {
        setTimerTasks(tasks.timer_task_names);
      }
    };

    void fetchNames();
  }, []);

  useEffect(() => {
    if (controlType === 'feature-flag' && featureFlags.length > 0) {
      setItemName(featureFlags[0]);
    } else if (controlType === 'timer-task' && timerTasks.length > 0) {
      setItemName(timerTasks[0]);
    }
  }, [controlType, featureFlags, timerTasks]);

  useEffect(() => {
    const fetchCurrentState = async () => {
      if (!itemName) return;

      try {
        if (controlType === 'feature-flag') {
          const state = await revereGetFeatureFlagState(itemName);
          setCurrentState(state ?? null);
        } else {
          const state = await revereGetTimerTaskState(itemName);
          setCurrentState(state ?? null);
        }
      } catch {
        setCurrentState(null);
      }
    };

    void fetchCurrentState();
  }, [itemName, controlType]);

  const handleSubmit = async () => {
    if (!itemName.trim()) {
      setMessage({ type: 'error', text: `Please select a ${controlType === 'feature-flag' ? 'feature flag' : 'timer task'}` });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      if (controlType === 'feature-flag') {
        const needsValue = ['ADD_TO_WHITELIST', 'REMOVE_FROM_WHITELIST', 'ADD_TO_BLACKLIST', 'REMOVE_FROM_BLACKLIST'].includes(featureFlagAction);
        if (needsValue && !inputValue.trim()) {
          setMessage({ type: 'error', text: 'Please enter a value for list operation' });
          setLoading(false);
          return;
        }
        await revereFeatureFlagCommand(featureFlagAction, itemName, needsValue ? inputValue : undefined);
      } else {
        if (timerTaskAction === 'SET_FREQUENCY') {
          if (!inputValue.trim() || isNaN(Number(inputValue))) {
            setMessage({ type: 'error', text: 'Please enter a valid number for frequency' });
            setLoading(false);
            return;
          }
          await revereTimerTaskCommand(timerTaskAction, itemName, Number(inputValue));
        } else {
          await revereTimerTaskCommand(timerTaskAction, itemName);
        }
      }

      setMessage({ type: 'success', text: `Action executed successfully` });
      setInputValue('');

      if (itemName) {
        if (controlType === 'feature-flag') {
          const state = await revereGetFeatureFlagState(itemName);
          setCurrentState(state ?? null);
        } else {
          const state = await revereGetTimerTaskState(itemName);
          setCurrentState(state ?? null);
        }
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Failed: ${error}` });
    } finally {
      setLoading(false);
    }
  };

  const needsInput = () => {
    if (controlType === 'feature-flag') {
      return ['ADD_TO_WHITELIST', 'REMOVE_FROM_WHITELIST', 'ADD_TO_BLACKLIST', 'REMOVE_FROM_BLACKLIST'].includes(featureFlagAction);
    }
    return timerTaskAction === 'SET_FREQUENCY';
  };

  const getInputPlaceholder = () => {
    if (controlType === 'feature-flag') {
      if (featureFlagAction.includes('WHITELIST')) return 'Enter whitelist value';
      if (featureFlagAction.includes('BLACKLIST')) return 'Enter blacklist value';
    }
    return 'Enter frequency in minutes';
  };

  return (
    <>
      <CompassHeader pageType={PageType.Revere} />
      <PageContainer>
        <PageHeader>
          <PageTitle>Revere</PageTitle>
          <PageSubtitle>Internal Admin Panel for Feature Flags & Timer Tasks</PageSubtitle>
        </PageHeader>

        {message && (
          <MessageBanner type={message.type}>
            {message.text}
          </MessageBanner>
        )}

        <ContentCard>
          <Section>
            <SectionTitle>Control Type</SectionTitle>
            <Select value={controlType} onChange={(e) => setControlType(e.target.value as ControlType)}>
              <option value="feature-flag">Feature Flag</option>
              <option value="timer-task">Timer Task</option>
            </Select>
          </Section>

          <Section>
            <SectionTitle>{controlType === 'feature-flag' ? 'Feature Flag Name' : 'Timer Task Name'}</SectionTitle>
            <Select
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
            >
              {controlType === 'feature-flag'
                ? featureFlags.map((flag) => (
                    <option key={flag} value={flag}>
                      {flag}
                    </option>
                  ))
                : timerTasks.map((task) => (
                    <option key={task} value={task}>
                      {task}
                    </option>
                  ))
              }
            </Select>
          </Section>

          {currentState && (
            <Section>
              <SectionTitle>Current State</SectionTitle>
              <StateDisplay>
                {controlType === 'feature-flag' ? (
                  <>
                    <StateItem><strong>Status:</strong> {(currentState as GetFeatureFlagStateResponse).is_enabled ? 'Enabled' : 'Disabled'}</StateItem>
                    <StateItem><strong>Rollout:</strong> {(currentState as GetFeatureFlagStateResponse).state_value}%</StateItem>
                    {(currentState as GetFeatureFlagStateResponse).whitelist && (currentState as GetFeatureFlagStateResponse).whitelist.length > 0 && (
                      <StateItem><strong>Whitelist:</strong> {(currentState as GetFeatureFlagStateResponse).whitelist.join(', ')}</StateItem>
                    )}
                    {(currentState as GetFeatureFlagStateResponse).blacklist && (currentState as GetFeatureFlagStateResponse).blacklist.length > 0 && (
                      <StateItem><strong>Blacklist:</strong> {(currentState as GetFeatureFlagStateResponse).blacklist.join(', ')}</StateItem>
                    )}
                  </>
                ) : (
                  <>
                    <StateItem><strong>Status:</strong> {(currentState as GetTimerTaskStateResponse).is_enabled ? 'Enabled' : 'Disabled'}</StateItem>
                    <StateItem><strong>Frequency:</strong> {(currentState as GetTimerTaskStateResponse).frequency_min} min</StateItem>
                    <StateItem><strong>Last Run:</strong> {new Date((currentState as GetTimerTaskStateResponse).last_run_ts).toLocaleString()}</StateItem>
                    <StateItem><strong>Last Success:</strong> {new Date((currentState as GetTimerTaskStateResponse).last_success_ts).toLocaleString()}</StateItem>
                    <StateItem><strong>Task Status:</strong> {(currentState as GetTimerTaskStateResponse).status}</StateItem>
                  </>
                )}
              </StateDisplay>
            </Section>
          )}

          <Section>
            <SectionTitle>Action</SectionTitle>
            {controlType === 'feature-flag' ? (
              <Select value={featureFlagAction} onChange={(e) => setFeatureFlagAction(e.target.value as FeatureFlagAction)}>
                <option value="ENABLE">Enable Flag</option>
                <option value="DISABLE">Disable Flag</option>
                <option value="ADD_TO_WHITELIST">Add to Whitelist</option>
                <option value="REMOVE_FROM_WHITELIST">Remove from Whitelist</option>
                <option value="ADD_TO_BLACKLIST">Add to Blacklist</option>
                <option value="REMOVE_FROM_BLACKLIST">Remove from Blacklist</option>
              </Select>
            ) : (
              <Select value={timerTaskAction} onChange={(e) => setTimerTaskAction(e.target.value as TimerTaskAction)}>
                <option value="ENABLE">Enable Task</option>
                <option value="DISABLE">Disable Task</option>
                <option value="SET_FREQUENCY">Set Frequency</option>
              </Select>
            )}
          </Section>

          {needsInput() && (
            <Section>
              <SectionTitle>{controlType === 'feature-flag' ? 'Input Value' : 'Frequency (minutes)'}</SectionTitle>
              <Input
                type={controlType === 'timer-task' && timerTaskAction === 'SET_FREQUENCY' ? 'number' : 'text'}
                placeholder={getInputPlaceholder()}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
              />
            </Section>
          )}

          <Section>
            <SubmitButton onClick={handleSubmit} disabled={loading || !itemName.trim() || (needsInput() && !inputValue.trim())}>
              {loading ? 'Executing...' : 'Submit'}
            </SubmitButton>
          </Section>
        </ContentCard>
      </PageContainer>
    </>
  );
};

const PageContainer = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 32px 24px;
`;

const PageHeader = styled.div`
  margin-bottom: 32px;
`;

const PageTitle = styled.h1`
  margin: 0 0 8px 0;
  font-size: 32px;
  font-weight: 700;
  color: #1A1D2B;
  background: linear-gradient(135deg, #02033B 0%, #743D98 60%, #AC326C 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const PageSubtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: #8890A5;
`;

const MessageBanner = styled.div<{ type: 'success' | 'error' }>`
  padding: 12px 16px;
  margin-bottom: 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  background: ${(props) => (props.type === 'success' ? '#d4edda' : '#f8d7da')};
  color: ${(props) => (props.type === 'success' ? '#155724' : '#721c24')};
  border: 1px solid ${(props) => (props.type === 'success' ? '#c3e6cb' : '#f5c6cb')};
`;

const ContentCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid #E4E7EF;
`;

const Section = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #1A1D2B;
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  border: 2px solid #E4E7EF;
  border-radius: 8px;
  background: white;
  color: #1A1D2B;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: #CDD1DC;
  }

  &:focus {
    outline: none;
    border-color: #008AFF;
  }
`;

const SubmitButton = styled.button<{ danger?: boolean }>`
  width: 100%;
  padding: 14px 24px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  background: ${(props) => (props.danger ? '#e53e3e' : '#008AFF')};
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${(props) => (props.danger ? '#c53030' : '#0070D6')};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  border: 2px solid #E4E7EF;
  border-radius: 8px;
  background: white;
  color: #1A1D2B;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: #CDD1DC;
  }

  &:focus {
    outline: none;
    border-color: #008AFF;
  }

  &::placeholder {
    color: #8890A5;
  }
`;

const StateDisplay = styled.div`
  background: #F8F9FC;
  border: 1px solid #E4E7EF;
  border-radius: 8px;
  padding: 16px;
`;

const StateItem = styled.div`
  margin-bottom: 8px;
  font-size: 14px;
  color: #1A1D2B;

  &:last-child {
    margin-bottom: 0;
  }

  strong {
    color: #4a5568;
    margin-right: 8px;
  }
`;

export default ReverePage;
